/**
 * 3D park geometry for the Pick-Your-Pitch three-dimensional view.
 * All shapes are traced from Justina's official site plan (LBWL 001) in the
 * same coordinate frame as the pin map: x/y are percent of the plan image.
 * `toWorld` converts percent → world units (y% becomes scene Z, ground = Y 0).
 */

export const SX = 0.62; // world units per percent of image width
export const SZ = 1.13; // world units per percent of image height

export const toWorld = (xPct, yPct) => [(xPct - 50) * SX, (yPct - 50) * SZ]; // [x, z]

/** Red park-boundary polygon, traced from the plan (percent coords). */
/** Park grass outline (percent coords) — follows the real hedgerow, i.e. the
    "black line" tree ring (TREES), so the green edge and the hedge sit exactly
    on the real hedge line. North edge keeps the original trace so the tent
    field stays inside; the west dog-walk lane and the entrance drive's outer
    end lie outside as farm tracks, matching reality. */
export const BOUNDARY = [[10.43, 7.48], [17.65, 1.45], [22.73, 5.74], [28.65, 7.6], [57.72, 7.54], [61.82, 11.4], [64.49, 38.35], [64.96, 47.34], [67.97, 74.05], [71.91, 81.62], [81.83, 82.49], [91.19, 82.75], [94.23, 89.49], [90.11, 94.19], [80.29, 93.21], [71.64, 95.34], [63.43, 93.1], [54.36, 88.52], [45.92, 83.89], [37.67, 81.1], [29.59, 76.87], [26.75, 71.74], [24.21, 63.69], [22.1, 55.02], [18.85, 47.06], [13.9, 39.2], [11.22, 30.18], [7.58, 21.02], [5.09, 12.17]];

/** Lake outlines (percent coords), hand-traced from the plan. */
export const LAKE_WALNUT = [[25.8, 28.9], [27.0, 26.5], [33.0, 26.0], [36.3, 26.9], [40.7, 25.8], [45.3, 26.1], [47.7, 27.8], [47.5, 30.4], [45.3, 31.9], [38.0, 32.4], [29.7, 32.2], [26.7, 30.9]];

export const LAKE_DAISY = [[42.2, 49.8], [53.0, 50.3], [58.5, 52.2], [59.0, 55.5], [58.2, 57.5], [57.0, 58.8], [58.5, 60.5], [59.4, 64.0], [58.0, 67.5], [55.1, 69.2], [50.5, 70.5], [46.5, 71.1], [42.0, 70.6], [39.2, 69.9], [36.6, 64.5], [35.5, 60.5], [35.8, 57.9], [37.0, 52.0], [39.5, 50.2]];

/** Daisy's little island (percent coords). */
export const DAISY_ISLAND = [[46.5, 59.0], [49.9, 59.3], [51.7, 60.7], [50.8, 62.4], [47.8, 62.8], [46.1, 61.2]];

/** Trees — boundary ring sampled from the plan plus lake-side copses;
    any position clashing with a pitch pin was removed. */
export const TREES = [[61.82, 11.4], [64.49, 38.35], [64.96, 47.34], [67.97, 74.05], [71.91, 81.62], [81.83, 82.49], [91.19, 82.75], [94.23, 89.49], [90.11, 94.19], [80.29, 93.21], [71.64, 95.34], [63.43, 93.1], [54.36, 88.52], [45.92, 83.89], [37.67, 81.1], [29.59, 76.87], [26.75, 71.74], [24.21, 63.69], [22.1, 55.02], [18.85, 47.06], [13.9, 39.2], [11.22, 30.18], [7.58, 21.02], [5.09, 12.17], [10.43, 7.48], [26.2, 42.2], [27.3, 47.0], [25.2, 50.6], [52.0, 74.5], [48.0, 75.5], [44.0, 76.0], [38.0, 74.0], [27.0, 63.0], [26.0, 67.0], [28.5, 71.5]];

/** Camera presets per view (world coords): [position, target]. */
export const CAMERA_VIEWS = {
  park: { pos: [38, 62, 90], target: [-6, 0, 4] },
  "Daisy Lake": { pos: [8, 17, 33], target: [-2.8, 0, 7.6] },
  "Walnut Lake": { pos: [3, 14, -5], target: [-8.6, 0, -24.7] },
};

import { TreePine, ShowerHead, Flame, Home, Hotel, Store, Wheat, Leaf, Trash2, Dog, ToyBrick, Tag } from "lucide-react";

/**
 * Editable map features — trees, buildings and amenities the admin can add,
 * move or remove from the pitch-map editor. Stored in site content
 * (`pitchmap.features`) as {id, type, label, x, y} in the same percent frame
 * as the pins, and rendered by the 3D model + the photo layers.
 */
export const FEATURE_TYPES = [
  { type: "tree", label: "Tree", defaultLabel: "" },
  { type: "washroom", label: "Washroom / WC", defaultLabel: "Washroom" },
  { type: "bbq", label: "BBQ area", defaultLabel: "BBQ area" },
  { type: "lodge", label: "Lodge building", defaultLabel: "" },
  { type: "villa", label: "Villa", defaultLabel: "Villa" },
  { type: "reception", label: "Reception / Office", defaultLabel: "Reception" },
  { type: "field", label: "Field", defaultLabel: "Field" },
  { type: "grass", label: "Grass area", defaultLabel: "" },
  { type: "bins", label: "Bin point", defaultLabel: "Bins" },
  { type: "dogwalk", label: "Dog walk", defaultLabel: "Dog walk" },
  { type: "play", label: "Play area", defaultLabel: "Play area" },
  { type: "custom", label: "Custom marker", defaultLabel: "Landmark" },
];

export const FEATURE_ICONS = {
  tree: TreePine,
  washroom: ShowerHead,
  bbq: Flame,
  lodge: Home,
  villa: Hotel,
  reception: Store,
  field: Wheat,
  grass: Leaf,
  bins: Trash2,
  dogwalk: Dog,
  play: ToyBrick,
  custom: Tag,
};

/** The park's trees become editable features — seeded from the traced set. */
export const DEFAULT_FEATURES = TREES.map(([x, y], i) => ({ id: `tree-${i + 1}`, type: "tree", label: "", x, y }));

/** Chip colours per feature type (editor pins + customer photo layers). */
export const FEATURE_CHIP_COLORS = {
  tree: "#4E7D3E",
  washroom: "#33546D",
  bbq: "#9A5B4F",
  lodge: "#9A6B45",
  villa: "#7A8CA3",
  reception: "#0F0E0E",
  field: "#A98A4B",
  grass: "#6A8E3F",
  bins: "#5B6238",
  dogwalk: "#7A5A8C",
  play: "#B76E79",
  custom: "#6A5F58",
};

/** Guest-facing lodge pin label: "Heron Lodge" → "Heron". */
export const lodgeShortName = (name) => (name || "").replace(/\s*lodge(s)?\s*$/i, "").trim() || "Lodge";
